import React, { useCallback, useEffect, useState } from 'react'
import '../../pre-work-order/pre-work-order.css';
import "../../../../utils/pdfjs-init"
import { Document, Page } from 'react-pdf';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import { APIToFetchProductionDrawingById, APIToPublishProductionDrawing } from '../../../../service/api-service';
import { APIResponseEntity } from '../../../../interface/api-responce';
import Loader from '../../../../components/loader';
import { IsNullOrUndefined, IsStringNullEmptyOrUndefined } from '../../../../utils/null-check';
import { PDFDocument } from 'pdf-lib';
import { MAX_SCALE, MIN_SCALE } from '../../../pdf-pin-design/pdf-annotation';
import useOverrideBrowserZoom from '../../../../hooks/useOverrideBrowserZoom';
import { PDFTransformEntity } from '../../../../interface/pdf-pin';
import { toast } from 'react-toastify';
import { toastOptObj } from '../../../../utils/constants';
import ProductionDrawingSuccessModel from './production-drawing-success-model';
import { removeSessionStorageItem } from '../../../../service/storage-service';
import { StorageKeyEnum } from '../../../../utils/enum';

type ProductionDrawingPreviewBasicDetailEntity = {
    workOrderNumber: string;
    orderNumber: string;
    projectName: string;
}

type ProductionDrawingFlowStateEntity = {
    sourcePath: string;
    workOrderId: string;
    selectedSolutionIndexes: string[];
};

const ProductionDrawingPreview = () => {
    const { id } = useParams();

    const navigate = useNavigate();
    const location = useLocation();
    const flowState = location.state as ProductionDrawingFlowStateEntity;

    const [isLoading, setIsLoading] = useState(false);

    const [transform, setTransform] = useState<PDFTransformEntity>({
        rotation: 0,
        scale: 1,
        x: 0,
        y: 0
    });

    const [isDragging, setIsDragging] = useState(false);

    const [basicDetails, setBasicDetails] = useState<ProductionDrawingPreviewBasicDetailEntity | null>(null);

    const [scale, setScale] = useState(1);

    const zoomIn = () => setScale((prev) => Math.min(prev + 0.1, MAX_SCALE));
    const zoomOut = () => setScale((prev) => Math.max(prev - 0.1, MIN_SCALE));

    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    const resetZoom = () => setScale(1);

    const [PDFUrl, setPDFUrl] = useState("");

    const [totalPages, setTotalPages] = useState(0);

    const [selectedPDFPage, setSelectedPDFPage] = useState(1);
    const [publishCount, setPublishCount] = useState(0);
    const [publishedWorkOrderId, setPublishedWorkOrderId] = useState("");
    const [isPublishModelOpen, setIsPublishModelOpen] = useState(false);

    const handleClose = () => setIsPublishModelOpen(false);
    const handleOpen = () => setIsPublishModelOpen(true);

    async function fetchProductionDrawingPDF() {
        setIsLoading(true);
        const resp: AxiosResponse<APIResponseEntity> = await APIToFetchProductionDrawingById(id as string);

        if (!resp || !resp.data || resp.status !== 200) { setIsLoading(false); return; }

        const apiRespPayload: APIResponseEntity = resp.data;

        if (!apiRespPayload.status || apiRespPayload.statusCode !== 200 || !apiRespPayload.data) {
            setIsLoading(false);
            return;
        }

        setPDFUrl(apiRespPayload.data.pdfURL || "");
        setBasicDetails({
            workOrderNumber: apiRespPayload.data.workOrderNumber || "",
            orderNumber: apiRespPayload.data.orderNumber || "",
            projectName: apiRespPayload.data.projectName || ""
        });

        const pdfBytes = await fetch(apiRespPayload.data.pdfURL).then(res => res.arrayBuffer());
        const pdfDoc = await PDFDocument.load(pdfBytes);

        setTotalPages(pdfDoc.getPageCount());
        setIsLoading(false);
    }

    async function handlePublishProductionDrawing() {
        setIsLoading(true);
        const resp: AxiosResponse<APIResponseEntity> = await APIToPublishProductionDrawing(id as string);
        setIsLoading(false);

        if (!resp || !resp.data || resp.status !== 200) {
            return;
        }

        const apiRespPayload: APIResponseEntity = resp.data;
        if (!apiRespPayload.status || apiRespPayload.statusCode !== 200) {
            toast.error(apiRespPayload.message || apiRespPayload.errorMsg, { ...toastOptObj });
            return;
        }

        setPublishCount(apiRespPayload.data && apiRespPayload.data.totalSolutionCounts ? apiRespPayload.data.totalSolutionCounts : 0);
        setPublishedWorkOrderId(apiRespPayload.data && apiRespPayload.data.workOrderId ? apiRespPayload.data.workOrderId : "");
        handleOpen();
    }

    useEffect(() => {
        fetchProductionDrawingPDF();
    }, [])

    const handleModelSubmit = () => {
        removeSessionStorageItem(StorageKeyEnum.STORE_SELECTED_SOLUTION_INDEXES_FOR_PD_READY);
        if (flowState && !IsStringNullEmptyOrUndefined(flowState.sourcePath)) {
            navigate(flowState.sourcePath, { replace: true });
            return;
        }
        navigate(`/production-work-order-list`, { replace: true });
    }

    const restartScale = () => {
        setTransform(() => ({
            x: 0,
            y: 0,
            rotation: 0,
            scale: 1
        }));
        setScale(1)
    }

    const nextDrawing = () => {
        let nextIndex = selectedPDFPage + 1;
        if (nextIndex > totalPages) {
            nextIndex = 1;
        }
        setSelectedPDFPage(nextIndex);
    };

    const prevDrawing = () => {
        let prevIndex = selectedPDFPage - 1;
        if (prevIndex < 1) {
            prevIndex = totalPages;
        }
        setSelectedPDFPage(prevIndex);
    };

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.button === 0) {
            setIsDragging(true);
            setDragStart({ x: e.clientX - transform.x, y: e.clientY - transform.y });
        }
    }, [transform.x, transform.y]);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (isDragging) {
            setTransform(prev => ({
                ...prev,
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            }));
        }
    }, [isDragging, dragStart]);

    useEffect(() => {
        const handleWindowMouseUp = () => {
            setIsDragging(false);
        };

        window.addEventListener("mouseup", handleWindowMouseUp);

        return () => {
            window.removeEventListener("mouseup", handleWindowMouseUp);
        };
    }, []);

    useOverrideBrowserZoom(zoomIn, zoomOut, restartScale);

    return (
        <>
            <Loader isLoading={isLoading} />
            <div className="d-flex flex-column" style={{ backgroundColor: "#F4F5F7", height: "100%", overflow: "hidden" }}>
                <div className="shop-drawing-header-menu d-flex align-items-center gap-2">
                    <div className="shop-drawing-header-sub flex-grow-1">
                        <div style={{ fontSize: "14px" }} dangerouslySetInnerHTML={{ __html: (basicDetails && !IsNullOrUndefined(basicDetails) ? `<b b style="padding-right:5px;">${basicDetails.projectName || ""}</b>${basicDetails.orderNumber ? `(<b style='font-size:12px'>Order No:</b><span style="font-size:12px"> ${basicDetails.orderNumber || ""}</span>)` : ""}${basicDetails.workOrderNumber ? ` <span style="font-size:12px">| <b style='font-size:12px'>WO No:</b> ${basicDetails.workOrderNumber || ""}</span>` : ""}` : "") }} />
                    </div>
                    <div className='d-flex justify-content-between gap-2 align-items-center'>
                        <div className='d-flex align-items-center gap-2 h-100 mx-2'>
                            <button className='sd-btn-button' onClick={zoomIn}>
                                <ZoomIn />
                            </button>
                            <button className='sd-btn-button' onClick={zoomOut}>
                                <ZoomOut />
                            </button>
                        </div>
                        <button className='btn btn-secondary' onClick={() => {
                            if (flowState && !IsStringNullEmptyOrUndefined(flowState.sourcePath) && !IsStringNullEmptyOrUndefined(flowState.workOrderId)) {
                                navigate(flowState.sourcePath, {
                                    replace: true,
                                    state: {
                                        ...flowState,
                                        restoreProductionSelection: true
                                    }
                                });
                                return;
                            }
                            navigate(-1);
                        }}>Back</button>
                    </div>
                </div>

                <div className='d-flex flex-column h-100 overflow-hidden' style={{ backgroundColor: "#F4F5F7" }}>
                    <div className='flex-grow-1'>
                        <div className='d-flex mt-3'>
                            <div
                                className="mx-auto"
                                onMouseDown={handleMouseDown}
                                onMouseMove={handleMouseMove}
                                style={{
                                    transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale}) `,
                                    transformOrigin: 'center',
                                    transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                                    cursor: isDragging ? "grabbing" : "grab"
                                }}
                            >
                                {
                                    PDFUrl && !IsStringNullEmptyOrUndefined(PDFUrl) && (
                                        <Document file={PDFUrl}>
                                            <Page
                                                pageNumber={selectedPDFPage}
                                                scale={scale}
                                                renderTextLayer={false}
                                                renderAnnotationLayer={false}
                                                className="shadow-sm pdf-page"
                                            />
                                        </Document>
                                    )
                                }
                            </div>
                        </div>
                    </div>

                    <div style={{
                        position: "absolute", bottom: "0",
                        left: "50%",
                        transform: "translateX(-50%)",
                    }}>
                        <div className='py-4 d-flex justify-content-center align-items-center'>
                            <div className='position-relative'>
                                <p className='text-center'>{selectedPDFPage}/{totalPages}</p>
                                <button className='sd-btn-button' onClick={prevDrawing} disabled={totalPages <= 1}>
                                    <ChevronLeft />
                                </button>
                                <button className='btn btn-primary mx-2' onClick={handlePublishProductionDrawing}>
                                    Publish Production Drawing
                                </button>
                                <button className='sd-btn-button' onClick={nextDrawing} disabled={totalPages <= 1}>
                                    <ChevronRight />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ProductionDrawingSuccessModel key={publishCount.toString()} handleSubmit={handleModelSubmit} publishCount={publishCount} isOpen={isPublishModelOpen} onClose={handleClose} />
        </>
    )
}

export default ProductionDrawingPreview
